import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import type { ApprovalRequest, ChatMessage, MultipartStep, Pill } from "../types";
import type { SlashCommand, SlashContext } from "../slashCommands";
import type { Channel } from "../../shared/host";
import { useCommandList } from "../slashCommands/useCommandList";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { ChatMessages } from "./ChatMessages";
import { ChatInputBar } from "./ChatInputBar";
import { ChatToaster } from "./ChatToaster";
import { ChatConfirmDialog } from "./ChatConfirmDialog";
import { GraphAccessBridge } from "./GraphAccessBridge";
import { ActionChipDock } from "../../integrations/deckorate/features/action-chips/components/ActionChipDock";
import { TransformChatFlow } from "../../integrations/deckorate/features/slide-transform/components/TransformChatFlow";
import type { ActionChip } from "../../integrations/deckorate/features/action-chips/types";
import {
  useActionChipDispatch,
  type OpenChipSheet,
  type OpenDeckorateView,
} from "../../integrations/deckorate/features/action-chips/hooks/useActionChipDispatch";
import type { ChipSheetId } from "../../integrations/deckorate/features/action-chips/chipTargets";
import { subscribeChatSheet } from "../../integrations/deckorate/features/action-chips/services/chatSheetBridge";
import { ConvertImageToChartSheet } from "../../integrations/deckorate/features/ConvertChart/components/ConvertImageToChartSheet";
import { formatMessageAge, type SkillSessionInfo } from "../skillMessageSession";
import type { SlideRewriteCard } from "../../integrations/powerpoint/features/rephrase-slide/types";
import type { SlideRewriteDecision } from "../../integrations/powerpoint/features/rephrase-slide/components/SlideRewriteCard";
import styles from "./ChatTabPanel.module.css";

interface ChatTabPanelProps {
  readonly messages: ChatMessage[];
  readonly streaming: boolean;
  readonly activeTool: string | null;
  /** Sanitised backend progress line (v2 `activity` events, AIVA-5929). */
  readonly activityLine?: string | null;
  /** AIVA-8194 — rule keys the latest doc-review scan was scoped to. */
  readonly docReviewScope?: readonly string[] | null;
  readonly chatEndRef: React.RefObject<HTMLDivElement>;
  /** Boot greeting (ST-02). Persists as the thread's first bubble; UI-only, never sent to the backend. */
  readonly greeting: string;
  readonly inputPlaceholder: string;
  readonly input: string;
  readonly onInputChange: (value: string) => void;
  readonly onSubmit: (e: FormEvent, attachedContent?: string, attachedFileName?: string) => void;
  readonly onPillClick: (pill: Pill) => void | Promise<void>;
  /** Ids of the name-candidate pills currently selected — drives each pill's
   *  toggled visual state. See useChat's selectedNameCandidateIds. */
  readonly selectedNameCandidateIds?: ReadonlySet<string>;
  /** Approve/Deny click on an in-chat approval card (v2 HITL, AIVA-5929). */
  readonly onApprovalDecision?: (
    messageId: string,
    approval: ApprovalRequest,
    decision: "approve" | "reject",
  ) => void | Promise<void>;
  /** SC-06 — Apply rewrites / Cancel on a slide rewrite confirm card. */
  readonly onSlideRewrite?: (messageId: string, card: SlideRewriteCard, decision: SlideRewriteDecision) => void;
  /** Run / skip / cancel on a sequential-multipart plan card. */
  readonly onMultipartStep?: (
    messageId: string,
    action: "run" | "skip" | "cancel",
    step?: MultipartStep,
  ) => void | Promise<void>;
  /** AIVA-6436 — Amend feedback on a Word write-pill approval card. */
  readonly onPillAmend?: (pill: Pill, feedback: string) => void | Promise<void>;
  readonly onPillBooked?: (messageId: string, pillLabel: string) => void;
  readonly host: Channel | null;
  readonly onSlashCommand: (cmd: SlashCommand) => void;
  readonly apiToken: string | null;
  readonly refreshTokens?: RefreshTokenFn;
  readonly hasWordSelection?: boolean;
  /** PowerPoint G-10: whether a shape with text is selected now (see ChatMessages). */
  readonly pptTextShapeSelected?: boolean | null;
  /** Outlook: true when an email is open in the reading pane — consulted by
   *  slash auto-send predicates when a palette item is picked. */
  readonly hasOpenEmail?: boolean;
  readonly onNewChat?: () => void;
  readonly onStop?: () => void;
  /** Re-runs the prompt behind a user-stopped reply (Regenerate button). */
  readonly onRegenerate?: (assistantMessageId: string) => void;
  readonly attachedFiles?: File[];
  readonly onAttachFiles?: (files: File[]) => void;
  readonly onRemoveFile?: (index: number) => void;
  readonly sessionFileCount?: number;
  readonly sessionFileBytes?: number;
  /** Persistent input-bar warning (oversized Word selection). */
  readonly contextNotice?: string | null;
  /**
   * PowerPoint only — opens a Deckorate feature screen for an action chip
   * (AIVA-6561). Owned by TaskPane because it means switching the active tab.
   * Omitted (Word/Outlook, or tests) leaves every chip a placeholder.
   */
  readonly onOpenDeckorateView?: OpenDeckorateView;
  /**
   * PowerPoint only — whether the Deckorate module is enabled for this user
   * (persona grant + Settings > Modules). The chip dock is Deckorate's, so it
   * renders only when this is true. Defaults to false: the dock stays hidden
   * unless the caller has confirmed the module is on.
   */
  readonly deckorateEnabled?: boolean;
  /** AIVA-7506 — the check-references thread, so an inline doc-review card
   *  can fetch its findings. */
  readonly docReviewThreadId?: string | null;
  readonly docReviewReady?: boolean;
  /** Set while a skill message's own chat session is on screen. */
  readonly skillSession?: SkillSessionInfo | null;
  /** Leave that session and restore the conversation it replaced. */
  readonly onReturnToPreviousChat?: () => void;
}

export function ChatTabPanel({
  messages,
  streaming,
  activeTool,
  activityLine = null,
  docReviewScope = null,
  chatEndRef,
  greeting,
  inputPlaceholder,
  input,
  onInputChange,
  onSubmit,
  onPillClick,
  selectedNameCandidateIds,
  onApprovalDecision,
  onMultipartStep,
  onSlideRewrite,
  onPillAmend,
  onPillBooked,
  host,
  onSlashCommand,
  apiToken,
  refreshTokens,
  hasWordSelection = false,
  pptTextShapeSelected = null,
  hasOpenEmail = false,
  onNewChat,
  onStop,
  onRegenerate,
  attachedFiles = [],
  onAttachFiles,
  onRemoveFile,
  sessionFileCount = 0,
  sessionFileBytes = 0,
  contextNotice = null,
  onOpenDeckorateView,
  deckorateEnabled = false,
  docReviewThreadId = null,
  docReviewReady = false,
  skillSession = null,
  onReturnToPreviousChat,
}: Readonly<ChatTabPanelProps>) {
  // Single subscription to the host's slash-command registry. This both
  // feeds the palette UI below AND warms the cache that lookupCommand in
  // useChat reads to resolve typed slash commands (e.g. `/find ...`) into a
  // forced_skill_key + stripped message body — one fetch per host serves
  // both consumers. On fetch failure the menu stays closed and typed
  // slashes fall back to natural-language routing.
  const { commands } = useCommandList(host, apiToken, refreshTokens);

  // The in-pane sheet a chip has opened, if any (AIVA-6561). Held here rather
  // than in TaskPane because a sheet does NOT change the active tab — it docks
  // over this panel, above the input bar, with the thread still visible.
  const [openSheet, setOpenSheet] = useState<ChipSheetId | null>(null);
  const openChipSheet = useCallback<OpenChipSheet>((sheet) => setOpenSheet(sheet), []);
  const closeChipSheet = useCallback(() => setOpenSheet(null), []);

  // The command palette ("All actions") can open a sheet too, from a different
  // tab — and, in its Office Dialog form, from a different window. It reaches
  // this panel through the bridge rather than through props, the same way chat
  // reaches a Deckorate panel (nav-bridge.ts). TaskPane subscribes separately to
  // switch the active tab here.
  useEffect(() => subscribeChatSheet(setOpenSheet), []);

  // Action-chip clicks. Called unconditionally (hooks rules) but only ever
  // exercised by the PowerPoint-gated dock below.
  const dispatchChip = useActionChipDispatch(onOpenDeckorateView, openChipSheet);

  // Channel state snapshot for the palette's auto-send predicates (e.g.
  // /catchup always auto-sends; Word /rewrite only with a selection).
  const slashContext = useMemo<SlashContext | undefined>(
    () => (host ? { host, hasOpenEmail, hasSelectedText: hasWordSelection } : undefined),
    [host, hasOpenEmail, hasWordSelection],
  );

  const sentHistory = useMemo(
    () => messages.filter((m) => m.role === "user").map((m) => m.content).reverse(),
    [messages],
  );

  const handleAttachFiles = useCallback((files: File[]) => {
    onAttachFiles?.(files);
  }, [onAttachFiles]);

  const handleRemoveFile = useCallback((index: number) => {
    onRemoveFile?.(index);
  }, [onRemoveFile]);

  const handleSubmit = useCallback((e: FormEvent) => {
    onSubmit(e);
  }, [onSubmit]);

  // Clear the input before firing the pill (AIVA-5058). A pill click is a
  // submit — it sends a new chat turn — so the input should be reset just
  // like the Send button does. This also unconditionally closes the
  // slash-command palette, which is driven by ``input.startsWith("/")``.
  //
  // name_candidate pills are the one exception: they're multi-select and
  // splice into whatever the user already typed rather than submitting
  // anything (useChat.handlePillClick owns that logic), so clearing first
  // would wipe the prefix/suffix text before that logic ever sees it.
  const handlePillClick = useCallback(
    (pill: Pill) => {
      if (pill.type !== "name_candidate") {
        onInputChange("");
      }
      return onPillClick(pill);
    },
    [onInputChange, onPillClick],
  );

  // Transform Slide runs as a CONVERSATION in this column (see
  // TransformChatFlow), not as a routed panel or an in-pane sheet, so it is
  // intercepted ahead of the shared dispatcher: chipTargets deliberately maps
  // this chip to neither a view nor a sheet, and without this it would fall
  // through to the dispatcher's "not available yet" placeholder toast.
  // Everything else still goes through that dispatcher, keeping its routing
  // and telemetry in one place.
  const [transformOpen, setTransformOpen] = useState(false);
  const closeTransform = useCallback(() => setTransformOpen(false), []);
  const handleChipSelect = useCallback(
    (chip: ActionChip) => {
      if (chip.id === "transformSlide") {
        setTransformOpen(true);
        return;
      }
      dispatchChip(chip);
    },
    [dispatchChip],
  );

  const skillMessageAge = skillSession ? formatMessageAge(skillSession.receivedAt) : "";
  const [docReviewFooterSlot, setDocReviewFooterSlot] = useState<HTMLDivElement | null>(null);

  return (
    <div className={styles.chatPanel}>
      {skillSession && (
        <section className={styles.sessionStrip} aria-label="Skill message conversation">
          {onReturnToPreviousChat && (
            <button type="button" className={styles.previousChat} onClick={onReturnToPreviousChat}>
              <svg viewBox="0 0 16 16" aria-hidden="true" className={styles.previousChatIcon}>
                <path d="M10 3.5L5.5 8l4.5 4.5" />
              </svg>
              Previous chat
            </button>
          )}
          <span className={styles.sessionSource}>
            From <strong>{skillSession.skillName || "a skill"}</strong>
            {skillMessageAge && <> · {skillMessageAge}</>}
          </span>
        </section>
      )}
      <ChatMessages
        docReviewFooterSlot={docReviewFooterSlot}
        messages={messages}
        streaming={streaming}
        activeTool={activeTool}
        activityLine={activityLine}
        docReviewScope={docReviewScope}
        chatEndRef={chatEndRef}
        greeting={skillSession ? "" : greeting}
        onPillClick={handlePillClick}
        selectedNameCandidateIds={selectedNameCandidateIds}
        onApprovalDecision={onApprovalDecision}
        onMultipartStep={onMultipartStep}
        onSlideRewrite={onSlideRewrite}
        onPillAmend={onPillAmend}
        onPillBooked={onPillBooked}
        onRegenerate={onRegenerate}
        hasWordSelection={hasWordSelection}
        pptTextShapeSelected={pptTextShapeSelected}
        preserveLineBreaks={host === "powerpoint"}
        docReviewThreadId={docReviewThreadId}
        docReviewReady={docReviewReady}
        apiToken={apiToken}
        refreshTokens={refreshTokens}
      />

      {/* Above the chips, sheets and composer docked below, so it covers none of them (AIVA-9183). */}
      <ChatToaster />

      {/* PowerPoint only. The chat panel is shared with Word and Outlook, where
          none of these slide actions exist, so users in those hosts must not see
          the dock at all. `host` is already resolved upstream in TaskPane via
          shared/host.ts — the single source of truth for the active add-in. */}
      {host === "powerpoint" && transformOpen && <TransformChatFlow onClose={closeTransform} />}

      {/* The chips are Deckorate's own actions, so the dock is hidden when the
          module is off — the general PowerPoint add-in shows no chips (AIVA-8744). */}
      {host === "powerpoint" && deckorateEnabled && <ActionChipDock onChipSelect={handleChipSelect} />}

      {/* In-pane sheets. PowerPoint-only for the same reason as the dock, and
          mounted between the dock and the input bar so the sheet docks to the
          bottom of the thread rather than covering the composer. Each sheet
          runs its own request only while open — see its `open` prop. */}
      {host === "powerpoint" && (
        <ConvertImageToChartSheet open={openSheet === "convert-chart"} onClose={closeChipSheet} />
      )}

      {/* The document review's "Copy as list" row renders here (a portal from
          DocReviewPanel), fixed above the input rather than scrolling with the
          thread. Empty, and so invisible, whenever no review is on screen. */}
      <div ref={setDocReviewFooterSlot} />

      <ChatInputBar
        input={input}
        onInputChange={onInputChange}
        onSubmit={handleSubmit}
        disabled={streaming}
        streaming={streaming}
        onStop={onStop}
        placeholder={inputPlaceholder}
        commands={commands}
        onSlashCommand={onSlashCommand}
        slashContext={slashContext}
        history={sentHistory}
        onNewChat={onNewChat}
        attachedFiles={attachedFiles}
        onAttachFiles={handleAttachFiles}
        onRemoveFile={handleRemoveFile}
        sessionFileCount={sessionFileCount}
        sessionFileBytes={sessionFileBytes}
        contextNotice={contextNotice}
      />

      <ChatConfirmDialog />
      <GraphAccessBridge />
    </div>
  );
}
